import { Dimension } from '@app/api/lib/resize';
import DocumentsApi, { DocumentDetail } from '@app/api/public/DocumentsApi';
import { useQueries, useQuery } from 'react-query';
import mime from 'mime';

const useGetSignedUrlByIdQueryKey = 'getSignedUrlById';

export const signedUrlByIdQueryKeys = {
    all: ['getSignedUrlById'],
    byId: (id?: string, dimension?: Dimension) => ['getSignedUrlById', { id, dimension }],
};

const getSignedUrl = (id?: string, dimension?: Dimension) => {
    if (!id) return Promise.reject('no id');

    return DocumentsApi.getSignedUrl(`${dimension ? dimension + '/' : ''}${id}`);
};

const useGetSignedUrlById = (id?: string, dimension?: Dimension, keepPreviousData = true) => {
    const mimeType = mime.getType(id || '');

    const imageDimension = mimeType?.includes('image') ? dimension : undefined;

    return useQuery(
        [signedUrlByIdQueryKeys.byId(id), `${imageDimension ? imageDimension + '/' : ''}${id}`],
        () => getSignedUrl(id, imageDimension),
        {
            enabled: !!id,
            keepPreviousData,
        }
    );
};

const getSignedUrlsStore = async (documents?: DocumentDetail[], demension?: Dimension) => {
    if (!documents?.length) return;
    const responses = await Promise.all(
        documents.map(async (document) => {
            return await DocumentsApi.getSignedUrlStore(
                `${demension ? demension + '/' : ''}${document?.document_data?.original?.id}`
            );
        })
    );

    return responses.filter((id) => !!id);
};

const useGetSignedUrls = (documents?: DocumentDetail[], demension?: Dimension) => {
    return useQuery(
        [useGetSignedUrlByIdQueryKey, `${demension ? demension + '/' : ''}`, { documents }],
        () => {
            return getSignedUrlsStore(documents, demension);
        },
        {
            enabled: !!documents?.length,
        }
    );
};

const getSignedUrlsStoreWithDocId = async (documents?: DocumentDetail[], demension?: Dimension) => {
    if (!documents?.length) return;
    const responses = await Promise.all(
        documents.map(async (document) => {
            const signedUrl = await DocumentsApi.getSignedUrlStore(
                `${demension ? demension + '/' : ''}${document?.document_data?.original?.id}`
            );
            return { id: document.id, signedUrl: signedUrl };
        })
    );

    return responses.filter((id) => !!id);
};

const useGetSignedUrlsWithDocId = (documents?: DocumentDetail[], demension?: Dimension) => {
    return useQuery(
        [useGetSignedUrlByIdQueryKey, `${demension ? demension + '/' : ''}`, { documents }],
        () => {
            return getSignedUrlsStoreWithDocId(documents, demension);
        },
        {
            enabled: !!documents?.length,
        }
    );
};

export { useGetSignedUrlById, useGetSignedUrls, useGetSignedUrlsWithDocId };
const useGetSignedUrlByIds = (ids?: string[], dimension?: Dimension) => {
    return useQueries(
        ids?.map((id) => {
            return {
                queryKey: signedUrlByIdQueryKeys.byId(id, dimension),
                queryFn: () => getSignedUrl(id, dimension),
            };
        }) || []
    );
};

export { useGetSignedUrlByIds, useGetSignedUrlById as default };
